import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Main from "../../components/layout/Main";
import { useToggleTop } from "../../components/common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { apiMessage } from "../../api/apiMessage";
import useAlive from "../../hooks/useAlive";
import "../../assets/styles/app-device.css";
import "../../assets/styles/app-preferences.css";

// The same limits the API checks (PUT /config/:id/address), as in the app.
const FIELDS = [
  { key: "line1", label: "House or flat, building and street", max: 120, required: true },
  { key: "line2", label: "Area or locality", max: 120 },
  { key: "landmark", label: "Landmark", max: 120 },
  { key: "city", label: "City", max: 60, required: true },
  { key: "state", label: "State", max: 60, required: true },
  { key: "pincode", label: "PIN code", max: 10, required: true, inputMode: "numeric" },
];
const EMPTY = Object.fromEntries(FIELDS.map(({ key }) => [key, ""]));

// { field: message } for what the API would refuse, so it shows before saving.
function problemsIn(values, country) {
  const problems = {};
  FIELDS.forEach(({ key, max, required }) => {
    const value = values[key].trim();
    if (required && !value) problems[key] = "This is required.";
    else if (value.length > max && key !== "pincode") problems[key] = `Keep it to ${max} characters.`;
  });
  if (!problems.pincode) {
    const pincode = values.pincode.replace(/\s/g, "");
    if (country === "IN" && !/^[1-9]\d{5}$/.test(pincode)) problems.pincode = "Enter a 6-digit PIN code.";
    else if (country !== "IN" && (pincode.length < 3 || pincode.length > 10)) problems.pincode = "Enter a valid postal code.";
  }
  return problems;
}

// The app's Home address screen (app/screens/HomeSettings/HomeAddress.js): the
// selected home's postal address, for its owners (shared owners too). The
// app's "Use current location" isn't here: a browser is often far from the
// home, and that position would also become the home's location for sunrise
// and sunset automations.
function HomeAddress() {
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const { profileName } = useUser();
  const homeId = User.getPropertyId();
  const home = profileName.find((item) => item.id === homeId);
  const [values, setValues] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [country, setCountry] = useState("IN");
  const [problems, setProblems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const changed = FIELDS.some(({ key }) => values[key] !== saved[key]);

  useEffect(() => {
    let current = true;
    if (!homeId) {
      setLoading(false);
      setError("Choose a home first.");
      return undefined;
    }
    setLoading(true);
    setError(null);
    UserService.getHomeAddress(homeId)
      .then((result) => {
        if (!current || !result?.address) return;
        const { country: savedCountry, updatedAt, ...address } = result.address;
        const filled = { ...EMPTY, ...address };
        setValues(filled);
        setSaved(filled);
        setCountry(savedCountry || "IN");
      })
      .catch((loadError) => current && setError(apiMessage(loadError, "Couldn't load the address. Try again.")))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [homeId]);

  const change = (key, value) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    setProblems((previous) => ({ ...previous, [key]: null }));
  };

  const save = async (event) => {
    event?.preventDefault();
    if (!changed || saving || !homeId) return;
    const found = problemsIn(values, country);
    setProblems(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    setError(null);
    const address = { country };
    FIELDS.forEach(({ key }) => {
      address[key] = values[key].trim();
    });
    address.pincode = address.pincode.replace(/\s/g, "");
    try {
      await UserService.saveHomeAddress(homeId, address);
    } catch (saveError) {
      if (!alive.current) return;
      setSaving(false);
      // 400 { message, field: "address.<name>" } points at one field
      const field = typeof saveError?.field === "string" ? saveError.field.replace("address.", "") : null;
      if (field && FIELDS.some(({ key }) => key === field)) {
        setProblems({ [field]: apiMessage(saveError, "Check this field.") });
      } else {
        setError(apiMessage(saveError, "Couldn't save the address. Try again."));
      }
      return;
    }
    if (!alive.current) return;
    toast("Address saved.", "green");
    history.goBack();
  };

  return (
    <Main
      header="stack"
      title="Home address"
      hideTabBar
      headerRight={
        <button type="submit" form="hx-pref-address" className="hx-edit-update" disabled={!changed || saving || loading}>
          {saving ? "Saving…" : "Save"}
        </button>
      }
    >
      {loading ? (
        <div className="hx-edit" style={{ display: "grid", gap: 18, paddingTop: 20 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="hx-skel" style={{ minHeight: 56 }} />
          ))}
        </div>
      ) : (
        <form id="hx-pref-address" className="hx-edit" onSubmit={save} noValidate>
          <p className="hx-pref-intro">
            {home?.name ? `Where ${home.name} is. ` : ""}It helps when we visit for service or a repair.
          </p>
          {FIELDS.map(({ key, label, max, required, inputMode }) => (
            <label key={key} className="hx-field">
              <span className="hx-field-label">
                {label}
                {required ? "" : " (optional)"}
              </span>
              <input
                className="hx-field-input"
                value={values[key]}
                onChange={(event) => change(key, event.target.value)}
                maxLength={max}
                inputMode={inputMode}
                autoComplete={{ line1: "address-line1", line2: "address-line2", city: "address-level2", state: "address-level1", pincode: "postal-code" }[key] ?? "off"}
                aria-invalid={problems[key] ? true : undefined}
                aria-describedby={problems[key] ? `hx-addr-${key}-problem` : undefined}
                disabled={saving}
              />
              {problems[key] ? (
                <p id={`hx-addr-${key}-problem`} className="hx-field-error">
                  {problems[key]}
                </p>
              ) : null}
            </label>
          ))}
          {error ? (
            <p className="hx-field-error" role="alert" style={{ marginTop: 22 }}>
              {error}
            </p>
          ) : null}
        </form>
      )}
    </Main>
  );
}

export default HomeAddress;
